import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import type { ConnectionDeleteResult } from '@mint-lib/integration-registry';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useNavigate } from '@mint/routing';

import { CONNECTION_PATHS } from '../connectionPaths.js';
import { useConnectionPlatform } from '../hooks/useConnectionPlatform.js';
import { useReadOnly } from '../hooks/useReadOnly.js';
import { ConnectionModal } from './ConnectionModal.js';
import { ManageConnectionHeader } from './ManageConnectionHeader.js';

interface Props {
  /** Platform URN — used to navigate back to its add page after a successful delete. */
  platform: string;
  connectionId: string;
  isUsingInAccounts: boolean;
  onReconnect: () => void;
  /** Hides both header buttons while an inline reconnect form is open (basic-flow platforms only). */
  isButtonsHidden?: boolean;
  /** Datadog action name for the reconnect button, forwarded unchanged; pass a per-platform literal. */
  reconnectActionName: string;
  remove: ConnectionDeleteResult['remove'];
  isRemovePending: boolean;
}

/**
 * `ManageConnectionHeader` (title + reconnect + remove) plus the remove
 * confirmation modal and its delete orchestration (snackbar, navigate back
 * to the add page on success) — identical across every registry manage form
 * (Amazon/Zemanta/the 9 generic omni platforms), which each only differ in
 * how their own `ConnectionRepository.useDelete()` and reconnect trigger are
 * wired. `remove`/`isRemovePending` come from the caller's own repository
 * instead of this component depending on `@mint-lib/omni-integration`
 * directly, which would create a circular package dependency.
 */
export function ManageConnectionActions({
  platform,
  connectionId,
  isUsingInAccounts,
  onReconnect,
  isButtonsHidden = false,
  reconnectActionName,
  remove,
  isRemovePending,
}: Props) {
  const { t } = useTranslation('@mint-lib/connections-ui');
  const isReadOnly = useReadOnly();
  const platformDisplay = useConnectionPlatform();
  const navigate = useNavigate();

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const toggleDeleteModalAppearance = () => {
    setIsDeleteModalVisible((isVisible) => !isVisible);
  };

  const handleDelete = () => {
    remove(connectionId, {
      onSuccess: () => {
        enqueueSnackbar({
          variant: 'success',
          title: t('Success'),
          subTitle: t('The account has been deleted successfully.'),
        });
        setIsDeleteModalVisible(false);
        void navigate({
          to: CONNECTION_PATHS.connectionsAdd,
          params: { integration: platform },
        });
      },
      onError: () => {
        enqueueSnackbar({
          variant: 'error',
          title: t('Error'),
          subTitle: t('Something went wrong. Please try again later.'),
        });
      },
    });
  };

  return (
    <>
      <ManageConnectionHeader
        platform={platformDisplay}
        isAllowedRemove={!isUsingInAccounts && !isReadOnly}
        isButtonsHidden={isButtonsHidden}
        onRemove={toggleDeleteModalAppearance}
        onReconnect={onReconnect}
        reconnectActionName={reconnectActionName}
      />
      <ConnectionModal
        isOpen={isDeleteModalVisible}
        onOpenChange={toggleDeleteModalAppearance}
        onSubmit={handleDelete}
        title={t('Delete connection?')}
        description={t('Are you sure you want to remove this connection?')}
        isSubmitDisabled={isRemovePending}
      />
    </>
  );
}
